import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { Grid, withStyles } from '@material-ui/core';

import Dashboard from '../../../../layouts/Dashboard';
import { GridCard, GridActionCard } from '../../../../components/Grid';
import CustomSortableTable, { SortableTableHeader, SortableTableRow } from '../../../../components/CustomSortableTable';
import CustomViewToggle, { ViewType } from '../../../../components/CustomViewToggle';

import { organisationsInitQuery, Organisation } from './queries';

import styles from './styles';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
}

const ListOrganisations = ({ classes, history }: Props): React.ReactElement => {
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [viewType, setViewType] = useState<ViewType>(ViewType.TABLE);

  const { data: organisationsInitData, loading } = useQuery(organisationsInitQuery, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && organisationsInitData) {
      setOrganisations(organisationsInitData.organisations);
    }
    return () => {
      mounted = false;
    };
  }, [organisationsInitData]);

  const handleCreate = () => {
    history.push('/admin/organisations/create');
  };

  const handleEdit = (id: string) => {
    history.push(`/admin/organisations/${id}/edit`);
  };

  const handleView = (id: string) => {
    history.push(`/admin/organisations/${id}`);
  };

  const tableHeaders: SortableTableHeader[] = [
    { key: 'name', label: 'Name' },
    { key: 'locations', label: 'Locations', align: 'center' },
  ];

  const tableRows: SortableTableRow[] = organisations.map((org) => ({
    key: org.id,
    columns: [
      { key: 'name', label: org.name },
      { key: 'locations', label: org.locations_aggregate.aggregate.count },
    ],
    actions: [
      { label: 'View', onClick: () => handleView(org.id) },
      { label: 'Edit', onClick: () => handleEdit(org.id) },
    ],
  }));

  return (
    <Dashboard title="Organisations">
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12}>
          <CustomViewToggle value={viewType} handleChangeView={setViewType} />
        </Grid>
        {viewType === ViewType.TABLE && (
          <Grid item xs={12}>
            <CustomSortableTable
              title="Organisations"
              counter={organisations.length}
              headers={tableHeaders}
              rows={tableRows}
              loading={loading}
              searchable={['name']}
              actions={[{ key: 'create', label: 'Create', onClick: () => handleCreate() }]}
            />
          </Grid>
        )}
        {viewType === ViewType.GRID && (
          <>
            <GridActionCard onClick={handleCreate} fill />
            {organisations.map((organisation: Organisation) => (
              <GridCard
                fill
                key={organisation.id}
                title={organisation.name}
                counter={organisation.locations_aggregate.aggregate.count}
                counterLabel="Locations"
                handleEdit={() => handleEdit(organisation.id)}
                handleView={() => handleView(organisation.id)}
              />
            ))}
          </>
        )}
      </Grid>
    </Dashboard>
  );
};

export default withRouter(withStyles(styles)(ListOrganisations));
